import {
  baseTicketCommentSchema,
  type GetTicketCommentsResponse,
} from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Warning } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  HTMLContainer,
  Rectangle2d,
  ShapeUtil,
  type RecordProps,
  type TLBaseShape,
  T,
  type TLShapeId,
} from "@tldraw/tldraw";
import cx from "classnames";
import { useEffect, useMemo } from "react";

import { isNil } from "../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { setActiveCommentId } from "../../../../utils/slices/ticket";
import { isCommentShapeVisible } from "../../../../utils/tldraw/comments";

export type TicketComment = GetTicketCommentsResponse[number];

type CommentShape = TLBaseShape<
  "comment",
  {
    w: number;
    h: number;
    commentId: unknown;
  }
>;

export class CommentAvatarShapeUtil extends ShapeUtil<CommentShape> {
  static override type = "comment-avatar" as const;

  static override props: RecordProps<CommentShape> = {
    w: T.number,
    h: T.number,
    commentId: T.unknown,
  };

  override isAspectRatioLocked = (_shape: CommentShape) => false;
  override canResize = (_shape: CommentShape) => false;
  override hideRotateHandle = (_shape: CommentShape) => true;
  override canEdit = () => true;

  getDefaultProps(): CommentShape["props"] {
    return {
      w: 25,
      h: 25,
      commentId: null,
    };
  }

  private isVisible(shape: CommentShape, userId?: string) {
    return isCommentShapeVisible(shape, userId, true);
  }

  getGeometry(shape: CommentShape) {
    const zoomLevel = this.editor.getZoomLevel();

    return new Rectangle2d({
      width: shape.props.w / zoomLevel,
      height: shape.props.h / zoomLevel,
      isFilled: true,
    });
  }

  component = (shape: CommentShape) => {
    const zoomLevel = this.editor.getZoomLevel();

    // Slices
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.value);
    const highlightedCommentId = useAppSelector(
      (state) => state.ticket.highlightedCommentId,
    );
    const comments = useAppSelector((state) => state.ticket.comments);

    // Local state
    const comment = useMemo(() => {
      if (isNil(shape.meta) || isNil(shape.meta.comment)) return null;

      try {
        const commentObj = JSON.parse(shape.meta.comment as string);
        const comment = baseTicketCommentSchema.parse(commentObj);

        const existingComment = comments.find((c) => comment._id === c._id);

        if (existingComment) {
          return existingComment;
        }

        return comment;
      } catch {
        return null;
      }
    }, [shape.meta?.comment]);

    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);

    // Effect chain

    useEffect(() => {
      if (isSelected) {
        this.editor.bringToFront([shape.meta.linkedCommentId as TLShapeId]);
        this.editor.setSelectedShapes([
          shape.id,
          shape.meta.linkedCommentId as TLShapeId,
        ]);
        if (!isNil(comment?._id)) {
          dispatch(setActiveCommentId(comment._id));
        }
      } else {
        dispatch(setActiveCommentId(null));
      }
    }, [comment, isSelected]);

    /////
    // No hooks beyond this point
    /////

    if (!this.isVisible(shape, user?.userID)) {
      return (
        <div
          style={{
            width: 0,
            height: 0,
          }}
        />
      );
    }

    const isRequired = comment ? comment.isRequired : !!shape.meta?.isRequired;
    const isAI = comment?.isAI ?? false;

    function getInitials(name: string | undefined) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      return parts
        .map((p) => p[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }

    function renderAvatar() {
      if (!comment || !isAI) {
        const name =
          !comment && user?.name
            ? user.name
            : typeof comment?.createdBy === "object" && comment.createdBy?.name
              ? comment.createdBy.name
              : user?.name;
        return (
          <Text size="1" weight="bold" className="leading-none text-base-white">
            {getInitials(name)}
          </Text>
        );
      }
      const Icon = isRequired ? Prohibit : Warning;
      return <Icon size={14} weight="fill" className="text-base-white" />;
    }

    const bgColor = isRequired
      ? "bg-puntt-red-9"
      : isAI
        ? "bg-puntt-gold-10"
        : "bg-puntt-accent-9";
    const shadowColor = isRequired
      ? "rgb(var(--puntt-red-9))"
      : isAI
        ? "rgb(var(--puntt-gold-10))"
        : "rgb(var(--puntt-accent-9))";

    return (
      <HTMLContainer
        style={{
          transform: `scale(${1 / zoomLevel})`,
          position: "relative",
          background: "transparent",
          border: "none",
        }}
      >
        <div className="comment-pin relative max-w-max">
          <div
            className={cx(
              "comment-pin flex size-[25px] items-center justify-center rounded-[20px_20px_20px_0]",
              bgColor,
            )}
            style={{
              transform: `rotate(-45deg)${highlightedCommentId === comment?._id ? " scale(1.25)" : ""}`,
              transformOrigin: "0% 100%",
              boxShadow: `0 0 0 2px ${shadowColor}, 0 1px 3px 2px rgba(0, 0, 0, 0.12), 0 1px 2px 2px rgba(0, 0, 0, 0.24)`,
            }}
          >
            <div style={{ transform: "rotate(45deg)" }}>{renderAvatar()}</div>
          </div>
        </div>
      </HTMLContainer>
    );
  };

  indicator = (_shape: CommentShape) => null;
}
